import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import EducationModal from '../components/EducationModal';
import toast from 'react-hot-toast';

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionParam = searchParams.get('session');
  const [step, setStep] = useState('role');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTooltip, setShowTooltip] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: '', color: '' });
  const [showEducation, setShowEducation] = useState(false);
  const [requirementsMet, setRequirementsMet] = useState({});
  const [checkingRequirements, setCheckingRequirements] = useState(false);
  const [isCurious, setIsCurious] = useState(false);
  const [showEmailOptional, setShowEmailOptional] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: null,
    farmName: '',
    phone: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    aboutText: '',
    tagline: '',
    inviteCode: '',
    abn: '',
    bankAccountName: '',
    bankBsb: '',
    bankAccountNumber: '',
    vehicleType: 'car',
    maxDeliveryDistance: 30,
    remunerationType: 'credit',
    bio: '',
    specialties: [],
    contentFocus: 'recipes',
    contentStyle: 'both',
    website: '',
    youtube: '',
    instagram: '',
    tiktok: '',
    linkedin: ''
  });

  // ✅ Store session if passed in URL
  useEffect(() => {
    if (sessionParam) {
      localStorage.setItem('ourpickup_session', sessionParam);
    }
  }, [sessionParam]);

  const roleCourseMap = {
    buyer: 'buyer-module-1-welcome',
    kin_coordinator: 'kin-module-1-what-is-a-kin',
    kin_member: 'kin-module-1-what-is-a-kin',
    producer: 'producer-module-1-welcome',
    hub: 'hub-module-1-welcome',
    pickup: 'pickup-module-1-welcome',
    educator: 'educator-module-1-welcome'
  };

  const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    let label = '';
    let color = '';
    if (score <= 2) { label = 'Weak'; color = 'bg-red-500'; }
    else if (score <= 4) { label = 'Medium'; color = 'bg-yellow-500'; }
    else { label = 'Strong'; color = 'bg-green-500'; }

    return { score, label, color, width: `${(score / 6) * 100}%` };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'password') {
      setPasswordStrength(calculatePasswordStrength(value));
    }
    if (error) setError('');
  };

  const checkRoleRequirements = async (selectedRole) => {
    if (!selectedRole) return;

    setCheckingRequirements(true);
    try {
      const courseSlug = roleCourseMap[selectedRole];
      if (!courseSlug) {
        setRequirementsMet({ [selectedRole]: true });
        return;
      }

      const sessionId = localStorage.getItem('ourpickup_session');
      const moduleRes = await fetch(`/api/education-modules/slug/${courseSlug}`);
      if (!moduleRes.ok) {
        setRequirementsMet({ [selectedRole]: false });
        return;
      }

      const moduleData = await moduleRes.json();
      const progressRes = await fetch(`/api/education/progress/${moduleData._id}`, {
        headers: {
          'X-Session-Id': sessionId || ''
        }
      });

      if (progressRes.ok) {
        const progressData = await progressRes.json();
        const isComplete = progressData.completed || false;
        setRequirementsMet({ [selectedRole]: isComplete });
      } else {
        setRequirementsMet({ [selectedRole]: false });
      }
    } catch (error) {
      console.error('Error checking role requirements:', error);
      setRequirementsMet({ [selectedRole]: false });
    } finally {
      setCheckingRequirements(false);
    }
  };

  const selectRole = (role) => {
    if (role === 'curious') {
      setIsCurious(true);
      setFormData(prev => ({ ...prev, role: 'buyer' }));
      setRequirementsMet({ buyer: true });
      setStep('details');
      setError('');
      return;
    }

    setIsCurious(false);
    setFormData(prev => ({ ...prev, role }));
    checkRoleRequirements(role);
    setStep('details');
    setError('');
  };

  const goBackToRoles = () => {
    setStep('role');
    setError('');
    setIsCurious(false);
    setShowEmailOptional(false);
  };

  const validateForm = () => {
    // For curious users - no validation needed, they can browse immediately
    if (isCurious) {
      return true;
    }

    // Regular validation for non-curious users
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (passwordStrength.score < 2) {
      setError('Password is too weak. Please use at least 8 characters with a mix of letters, numbers, and symbols.');
      return false;
    }
    const problematicChars = /[",'{}[\]()]/;
    if (problematicChars.test(formData.password)) {
      setError('Password contains invalid characters. Please use only letters, numbers, and standard punctuation (!@#$%^&*).');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (formData.name.length < 2) {
      setError('Please enter your full name');
      return false;
    }

    if (formData.role === 'kin_member' && !formData.inviteCode) {
      setError('Please enter your Kin invite code');
      return false;
    }
    if (formData.role === 'kin_member' && !formData.address) {
      setError('Please enter your address to verify you are within the Kin\'s area');
      return false;
    }
    if ((formData.role === 'producer' || formData.role === 'hub') && !formData.address) {
      setError('Please enter your business address for deliveries');
      return false;
    }
    if ((formData.role === 'producer' || formData.role === 'hub') && !formData.abn) {
      setError('Please enter your ABN for payment processing');
      return false;
    }
    if ((formData.role === 'producer' || formData.role === 'hub') && !formData.bankAccountNumber) {
      setError('Please enter your bank account details for payments');
      return false;
    }
    if (formData.role === 'pickup' && !formData.address) {
      setError('Please enter your address for pickup assignments');
      return false;
    }
    if (formData.role === 'pickup' && formData.remunerationType === 'cash' && !formData.bankAccountNumber) {
      setError('Please enter your bank account details for cash payments');
      return false;
    }
    if (formData.role === 'pickup' && formData.remunerationType === 'cash' && !formData.abn) {
      setError('Please enter your ABN for cash payments (required for tax reporting)');
      return false;
    }

    return true;
  };

  const handleEducationComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch('/api/education/init', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      await fetch('/api/education/track', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          modalType: 'overview',
          action: 'complete'
        })
      });
    } catch (error) {
      console.error('Error tracking education:', error);
    }

    navigate('/products');
  };

  // Handle curious browsing - no email, no name, just go
  const handleCuriousBrowse = () => {
    // Generate a session ID for the curious user
    let sessionId = localStorage.getItem('ourpickup_session');
    if (!sessionId) {
      sessionId = 'curious_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('ourpickup_session', sessionId);
    }
    
    // Track that this is a curious session
    localStorage.setItem('curious_mode', 'true');
    
    // Redirect to browse
    navigate('/products?curious=true');
  };

  // Handle email capture for curious users who want newsletters
// In client/src/pages/Register.js
const handleEmailCapture = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');

  try {
    const email = formData.email;
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Store locally first
    localStorage.setItem('curious_email', email);
    localStorage.setItem('curious_name', formData.name || 'Curious User');

    // Save to backend
    const sessionId = localStorage.getItem('ourpickup_session');
    const response = await fetch('/api/session/save-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId,
        email,
        name: formData.name || 'Curious User'
      })
    });

    if (!response.ok) {
      throw new Error('Failed to save email');
    }

    // Send confirmation email
    const emailResponse = await fetch('/api/session/send-reminder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email,
        sessionId
      })
    });

    setShowEmailOptional(false);
    setLoading(false);
    
    // ✅ Show success toast
    toast.success('📧 Email saved! Check your inbox for confirmation.');
    
    // ✅ Update the UI to show Curious+ status immediately
    // Force re-render to show updated status
    setTimeout(() => {
      window.location.reload();
    }, 1500);
    
  } catch (err) {
    console.error('Error saving email:', err);
    setError('Failed to save email. Please try again.');
    setLoading(false);
  }
};

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    // Check if requirements are met
    if (requirementsMet[formData.role] === false) {
      setError(`Please complete the ${formData.role.replace('_', ' ')} course before registering.`);
      return;
    }

    setLoading(true);

    try {
      const userData = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        phone: formData.phone || '',
        address: formData.address || '',
        suburb: formData.suburb || '',
        state: formData.state || '',
        postcode: formData.postcode || '',
        inviteCode: formData.inviteCode || '',
        metadata: {
          userType: 'buyer',
          onboardingStep: 'education',
          newsletterSubscribed: false,
          completedCourse: roleCourseMap[formData.role]
        }
      };

      if (formData.role === 'producer') {
        userData.farmName = formData.farmName || '';
        userData.abn = formData.abn || '';
        userData.bankAccountName = formData.bankAccountName || '';
        userData.bankBsb = formData.bankBsb || '';
        userData.bankAccountNumber = formData.bankAccountNumber || '';
        userData.aboutText = formData.aboutText || '';
        userData.tagline = formData.tagline || '';
      }

      if (formData.role === 'hub') {
        userData.abn = formData.abn || '';
        userData.bankAccountName = formData.bankAccountName || '';
        userData.bankBsb = formData.bankBsb || '';
        userData.bankAccountNumber = formData.bankAccountNumber || '';
        userData.aboutText = formData.aboutText || '';
        userData.tagline = formData.tagline || '';
      }

      if (formData.role === 'pickup') {
        userData.vehicleType = formData.vehicleType || 'car';
        userData.maxDeliveryDistance = formData.maxDeliveryDistance || 30;
        userData.remunerationType = formData.remunerationType || 'credit';
        userData.pickupSettings = {
          vehicleType: formData.vehicleType || 'car',
          maxDeliveryDistanceKm: formData.maxDeliveryDistance || 30,
          isAvailable: true,
          completedDeliveries: 0,
          rating: 5.0
        };
        if (formData.remunerationType === 'cash') {
          userData.abn = formData.abn || '';
          userData.bankAccountName = formData.bankAccountName || '';
          userData.bankBsb = formData.bankBsb || '';
          userData.bankAccountNumber = formData.bankAccountNumber || '';
        }
      }

      if (formData.role === 'educator') {
        userData.bio = formData.bio || '';
        userData.specialties = formData.specialties || [];
        userData.contentFocus = formData.contentFocus || 'recipes';
        userData.contentStyle = formData.contentStyle || 'both';
        userData.website = formData.website || '';
        userData.youtube = formData.youtube || '';
        userData.instagram = formData.instagram || '';
        userData.tiktok = formData.tiktok || '';
        userData.linkedin = formData.linkedin || '';
      }

      const result = await register(userData);

      if (formData.role === 'kin_coordinator') {
        navigate('/kin-starter');
      } else if (formData.role === 'kin_member') {
        navigate('/kin-join');
      } else {
        navigate('/login?registered=true');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    {
      value: 'curious',
      icon: '👀',
      label: "I'm Curious",
      description: 'Browse instantly - no email, no password, no cookies required',
      tooltip: '✨ As Curious you can: Browse products, take any course, earn credits (saved locally). No email needed. When you\'re ready, add email for newsletters or upgrade to a full role.',
      highlight: true,
      noCourseRequired: true,
      badge: '🚀 Instant Access'
    },
    {
      value: 'buyer',
      icon: '🛒',
      label: 'Buyer',
      description: 'Shop for fresh produce from local farms and hubs',
      tooltip: '🛒 As a Buyer you can: Purchase products, earn credits, see order history. Requires completing the Buyer course (10-15 mins).',
      course: 'buyer-module-1-welcome',
      badge: '📚 Course Required'
    },
    {
      value: 'kin_coordinator',
      icon: '🌟',
      label: 'Kin Coordinator',
      description: 'Start and lead a buying group',
      tooltip: '🌟 As a Kin Coordinator you can: Create a Kin, invite members, coordinate group orders. Requires completing the Kin Coordinator course.',
      course: 'kin-module-1-what-is-a-kin',
      badge: '📚 Course Required'
    },
    {
      value: 'kin_member',
      icon: '👥',
      label: 'Kin Member',
      description: 'Join an existing buying group',
      tooltip: '👥 As a Kin Member you can: Join a Kin, participate in group orders, get volume discounts. Requires completing the Kin Member course.',
      course: 'kin-module-1-what-is-a-kin',
      badge: '📚 Course Required'
    },
    {
      value: 'producer',
      icon: '🚜',
      label: 'Producer',
      description: 'Sell your farm products',
      tooltip: '🚜 As a Producer you can: List products, manage inventory, fulfill orders. Requires completing the Producer course and providing ABN/bank details.',
      course: 'producer-module-1-welcome',
      badge: '📚 Course Required'
    },
    {
      value: 'hub',
      icon: '🏪',
      label: 'Hub',
      description: 'Aggregate and sell from multiple producers',
      tooltip: '🏪 As a Hub you can: Connect producers with buyers, manage orders, set commissions. Requires completing the Hub course and providing ABN/bank details.',
      course: 'hub-module-1-welcome',
      badge: '📚 Course Required'
    },
    {
      value: 'pickup',
      icon: '📦',
      label: 'Pickup',
      description: 'Deliver orders and earn credits or cash',
      tooltip: '📦 As a Pickup you can: Claim delivery jobs, earn rewards, track earnings. Requires completing the Pickup course.',
      course: 'pickup-module-1-welcome',
      badge: '📚 Course Required'
    },
    {
      value: 'educator',
      icon: '🎓',
      label: 'Educator',
      description: 'Create educational content about food, farming, and nutrition',
      tooltip: '🎓 As an Educator you can: Create courses, build an audience, partner with producers. Requires completing the Educator course.',
      course: 'educator-module-1-welcome',
      badge: '📚 Course Required'
    }
  ];

  if (showEducation) {
    return (
      <EducationModal
        type="overview"
        onComplete={handleEducationComplete}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-8">
        {step === 'role' ? (
          <>
            <h1 className="text-3xl font-bold mb-2">Choose Your Path</h1>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-blue-700 text-sm">
                💡 <strong>Not sure yet?</strong> Choose <strong>"I'm Curious"</strong> to browse instantly - no email, no password, no cookies required.
              </p>
            </div>

            <div className="space-y-3">
              {roles.map(role => (
                <div key={role.value} className="relative">
                  <button
                    onClick={() => selectRole(role.value)}
                    className={`w-full text-left p-4 border rounded-lg hover:bg-gray-50 transition flex items-start gap-4 group ${
                      role.highlight ? 'border-green-400 bg-green-50 hover:bg-green-100' : ''
                    }`}
                  >
                    <span className="text-2xl flex-shrink-0">{role.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-semibold text-lg ${role.highlight ? 'text-green-700' : ''}`}>
                          {role.label}
                        </span>
                        {role.highlight && (
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full">
                            Recommended
                          </span>
                        )}
                        {role.noCourseRequired && (
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                            {role.badge || '🚀 No Course Required'}
                          </span>
                        )}
                        {role.course && !role.noCourseRequired && (
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                            {role.badge || '📚 Course Required'}
                          </span>
                        )}
                        <button
                          type="button"
                          className="text-gray-400 hover:text-gray-600 text-sm w-5 h-5 rounded-full border border-gray-300 flex items-center justify-center flex-shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowTooltip(showTooltip === role.value ? null : role.value);
                          }}
                        >
                          ?
                        </button>
                      </div>
                      <p className="text-gray-600 mt-1">{role.description}</p>
                    </div>
                    <span className="text-gray-300 group-hover:text-green-500 transition">→</span>
                  </button>

                  {showTooltip === role.value && (
                    <div className="absolute left-0 right-0 mt-1 p-3 bg-gray-800 text-white text-sm rounded-lg shadow-lg z-10">
                      <div className="flex items-start gap-2">
                        <span className="text-lg">💡</span>
                        <span className="whitespace-pre-wrap">{role.tooltip}</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Link to="/roles-explained" className="text-blue-500 hover:text-blue-700 text-sm underline">
                Learn more about each role →
              </Link>
            </div>
          </>
        ) : (
          <>
            <button
              onClick={goBackToRoles}
              className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-2"
            >
              ← Back to roles
            </button>

            {isCurious ? (
              <>
                <h1 className="text-3xl font-bold mb-2">👀 Browse Instantly</h1>
                
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                  <p className="text-purple-800 font-medium">🔍 No email, no password, no cookies required</p>
                  <ul className="text-sm text-purple-700 mt-2 space-y-1">
                    <li>✅ Browse products and storefronts instantly</li>
                    <li>✅ Take any course (earn credits saved locally)</li>
                    <li>✅ Your progress is saved in your browser</li>
                    <li>📬 Add your email anytime for newsletters and to save credits permanently</li>
                  </ul>
                </div>

                {/* Privacy Statement */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-600">
                    <strong>🔒 Our Privacy Commitment</strong>
                  </p>
                  <ul className="text-xs text-gray-500 mt-1 space-y-1">
                    <li>• We do <strong>not</strong> require cookies to browse</li>
                    <li>• We do <strong>not</strong> require your email unless you want to hear from us</li>
                    <li>• Your browsing data stays in your browser unless you choose to share it</li>
                    <li>• No tracking, no ads, no data selling</li>
                  </ul>
                </div>

                {/* Two options: Browse now, or add email */}
                <div className="space-y-4">
                  <button
                    onClick={handleCuriousBrowse}
                    className="w-full bg-purple-600 text-white py-4 rounded-lg hover:bg-purple-700 transition text-lg font-medium"
                  >
                    🚀 Start Browsing Now
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or</span>
                    </div>
                  </div>

                  <button
                    onClick={() => setShowEmailOptional(!showEmailOptional)}
                    className="w-full border-2 border-purple-300 text-purple-700 py-3 rounded-lg hover:bg-purple-50 transition"
                  >
                    📬 Add Email for Newsletter & Credit Saving
                  </button>

                  {showEmailOptional && (
                    <div className="bg-white border border-purple-200 rounded-lg p-4 mt-2">
                      <p className="text-sm text-gray-600 mb-3">
                        We'll send occasional updates about local food and your credits.
                        No spam, unsubscribe anytime.
                      </p>
                      <form onSubmit={handleEmailCapture} className="space-y-3">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Your Name (optional)</label>
                          <input 
                            type="text" 
                            name="name" 
                            value={formData.name} 
                            onChange={handleChange} 
                            className="w-full border rounded-md px-3 py-2" 
                            placeholder="What should we call you?"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Email Address *</label>
                          <input 
                            type="email" 
                            name="email" 
                            value={formData.email} 
                            onChange={handleChange} 
                            className="w-full border rounded-md px-3 py-2" 
                            placeholder="your@email.com"
                            required 
                          />
                        </div>
                        {error && (
                          <div className="text-red-500 text-sm">{error}</div>
                        )}
                        <button
                          type="submit"
                          disabled={loading}
                          className="w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                        >
                          {loading ? 'Saving...' : '💾 Save Email & Continue Browsing'}
                        </button>
                        <p className="text-xs text-gray-400 text-center">
                          Your credits will be saved to this email if you upgrade later.
                        </p>
                      </form>
                    </div>
                  )}
                </div>

                <div className="mt-4 text-center">
                  <button
                    onClick={goBackToRoles}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    ← Choose a different path
                  </button>
                </div>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold mb-2">
                  {roles.find(r => r.value === formData.role)?.icon} Create Your Account
                </h1>
                <p className="text-gray-600 mb-6">
                  {roles.find(r => r.value === formData.role)?.description}
                </p>

                {error && (
                  <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                  </div>
                )}

                {formData.role && requirementsMet[formData.role] === false && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-yellow-800 font-medium">⚠️ Course Required</p>
                    <p className="text-yellow-700 text-sm mt-1">
                      You must complete the <strong>{formData.role.replace('_', ' ')} course</strong> before registering for this role.
                      The course takes about 10-15 minutes and covers everything you need to know.
                    </p>
                    <Link
                      to={`/learn/${roleCourseMap[formData.role]}`}
                      className="inline-block mt-2 text-sm bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700"
                    >
                      Take the Course →
                    </Link>
                  </div>
                )}

                {checkingRequirements && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-blue-700">Checking course requirements...</p>
                  </div>
                )}

                {formData.role && requirementsMet[formData.role] === true && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <p className="text-green-700">✅ Course completed! You can now register for this role.</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-700 mb-2">Full Name *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2">Email *</label>
                    <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required />
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2">Password *</label>
                    <input type="password" name="password" value={formData.password} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required />
                    {formData.password && (
                      <div className="mt-2">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{ width: passwordStrength.width }} />
                        </div>
                        <p className={`text-xs mt-1 ${passwordStrength.color.replace('bg-', 'text-')}`}>
                          Password strength: {passwordStrength.label}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-gray-700 mb-2">Confirm Password *</label>
                    <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className="w-full border rounded-md px-3 py-2" required />
                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                      <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
                    )}
                  </div>

                  {/* Role-specific fields - keep all existing ones from original Register.js */}
                  {/* ... (all the producer, hub, pickup, educator fields) ... */}
                  
                  {/* For brevity in the cat command, I've included a placeholder - you'll need to merge with your existing role fields */}
                  <div className="text-sm text-gray-500">
                    <p>Role-specific fields would appear here (producer, hub, pickup, educator details).</p>
                    <p>These are unchanged from your existing Register.js.</p>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || requirementsMet[formData.role] === false}
                    className={`w-full text-white py-3 rounded-lg transition ${
                      requirementsMet[formData.role] === false
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </button>
                </form>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
